'use client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Section, HubItem, DocumentHit } from '@workspace/interfaces';
import { AccordionWithCustomContent } from '@workspace/ui/common/AccordionWithCustomContent';
import { DocumentHubItem } from '@workspace/widgets';
import { Configure, Hits, Index, useInstantSearch, useSearchBox } from 'react-instantsearch';
import { ALGOLIA_CONFIG } from '@/app/lib';
import { Hit as AlgoliaHit } from 'instantsearch.js/es/types';
import { AlgoliaShowMore, NoResultsBoundary } from '@workspace/widgets';
import { Typography } from '@workspace/ui/common';

interface DocumentHubProps {
  sections: Section[];
  className?: string;
  filter?: (item: HubItem, section: Section) => boolean;
  pageSlug?: string;
  filterActive?: boolean;
  onTagsDiscovered?: (tags: { id: string; label: string }[]) => void;
  downloadCTA?: string;
  viewCTA?: string;
  selectedTagIds?: string[];
}

const HitComponent = ({
  hit,
  downloadCTA,
  viewCTA,
}: {
  hit: AlgoliaHit<DocumentHit>;
  downloadCTA?: string;
  viewCTA?: string;
}) => {
  const item = useMemo<HubItem>(() => {
    return {
      id: hit.objectID,
      title: hit.title,
      image: hit.thumbnailImage ?? null,
      filename: hit.title,
      fileUrl: hit.fileUrl || '',
      tags: hit.documentTags?.map((tag) => tag.name) || [],
    };
  }, [hit]);
  return <DocumentHubItem item={item} downloadCTA={downloadCTA} viewCTA={viewCTA} />;
};

const buildAdditionalFilterQuery = (additionalFilters: string[]) => {
  const escapeValue = (v: string) => v?.replace(/"/g, '\\"');
  if (additionalFilters && additionalFilters.length > 0) {
    const parts = additionalFilters
      .map((tag) => {
        const containsField = /\w+\s*:\s*"?.+"/;
        if (containsField.test(tag)) return tag;
        return `documentTags.name:"${escapeValue(tag)}"`;
      })
      .filter(Boolean);
    return parts.length > 1 ? `(${parts.join(' OR ')})` : parts[0] || '';
  }
  return '';
};

const buildAutoFilterQuery = (autoFilter: Section['autoFilter']) => {
  const escapeValue = (v: string) => v?.replace(/"/g, '\\"');
  if (autoFilter) {
    const clauses: string[] = [];
    Object.entries(autoFilter).forEach(([key, values]) => {
      if (!values || values.length === 0) return;
      values.forEach((v) => {
        const val = v?.name;
        if (!val) return;
        clauses.push(`${key}.name:"${escapeValue(val)}"`);
      });
    });
    if (clauses.length === 1) return clauses[0];
    else if (clauses.length > 1) return `(${clauses.join(' OR ')})`;
  }
  return '';
};

const SectionNoResultsBoundary = ({
  indexId,
  hasManual,
  hasAuto,
  children,
}: {
  indexId: string;
  hasManual: boolean;
  hasAuto: boolean;
  children: React.ReactNode;
}) => {
  const { scopedResults, status } = useInstantSearch();

  const { matchedAny, sawReal, hitsCount } = useMemo(() => {
    let hitsCount = 0;
    let matchedAny = false;
    let sawReal = false;

    scopedResults.forEach((sr) => {
      const isManualIndex = hasManual && sr.indexId === `${indexId}__manual`;
      const isAutoIndex = hasAuto && sr.indexId === `${indexId}__auto`;
      if (!isManualIndex && !isAutoIndex) return;

      matchedAny = true;
      if (sr.results && !sr.results.__isArtificial) {
        sawReal = true;
        hitsCount += sr.results.nbHits || 0;
      }
    });

    return { matchedAny, sawReal, hitsCount };
  }, [scopedResults, indexId, hasManual, hasAuto]);

  const isSearching = status === 'loading' || status === 'stalled';
  const isEmpty = matchedAny && sawReal && hitsCount === 0;
  const [showEmpty, setShowEmpty] = useState(isEmpty && !isSearching);

  useEffect(() => {
    if (!isSearching) setShowEmpty(isEmpty);
  }, [isSearching, isEmpty]);

  return (
    <>
      {showEmpty && (
        <Typography className="text-center" variant="bodyMd">
          No documents available in this section
        </Typography>
      )}
      <div className={showEmpty ? 'hidden' : 'contents'}>{children}</div>
    </>
  );
};

const ManualSectionIndex = React.memo(function ManualSectionIndex({
  indexId,
  manualFiles,
  additionalFilters,
  filterActive,
  downloadCTA,
  viewCTA,
}: {
  indexId: string;
  manualFiles: NonNullable<Section['documentFiles']>;
  additionalFilters: string[];
  filterActive?: boolean;
  downloadCTA?: string;
  viewCTA?: string;
}) {
  const { query } = useSearchBox();
  const BoundHitComponentCallback = React.useCallback(
    ({ hit }: { hit: AlgoliaHit<DocumentHit> }) => (
      <HitComponent hit={hit} downloadCTA={downloadCTA} viewCTA={viewCTA} />
    ),
    [downloadCTA, viewCTA],
  );

  const filterQuery = useMemo(() => {
    const escapeValue = (v: string) => v?.replace(/"/g, '\\"');
    const fileIds = manualFiles.map((file) => file._ref).filter(Boolean);
    if (fileIds.length === 0) return '';
    const manualQuery = fileIds.map((ref) => `fileId:"${escapeValue(ref)}"`).join(' OR ');
    const manualQueryWrapped = fileIds.length > 1 ? `(${manualQuery})` : manualQuery;

    const additionalQuery = buildAdditionalFilterQuery(additionalFilters);

    if (manualQueryWrapped && additionalQuery) {
      return `${manualQueryWrapped} AND ${additionalQuery}`;
    }
    return manualQueryWrapped || additionalQuery || '';
  }, [manualFiles, additionalFilters]);

  const transformItems = useCallback(
    (items: AlgoliaHit<DocumentHit>[]) => {
      if (filterActive || query.trim()) {
        return items;
      }
      const fileOrder = manualFiles.map((file) => file._ref).filter(Boolean);
      return [...items].sort((a, b) => {
        const aId = a.fileId || a.objectID;
        const bId = b.fileId || b.objectID;
        const aIndex = fileOrder.indexOf(aId);
        const bIndex = fileOrder.indexOf(bId);

        const valA = aIndex === -1 ? Infinity : aIndex;
        const valB = bIndex === -1 ? Infinity : bIndex;
        return valA - valB;
      });
    },
    [manualFiles, filterActive, query],
  );

  if (!filterQuery) return null;

  return (
    <Index indexName={ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.Documents} indexId={`${indexId}__manual`}>
      <Configure filters={filterQuery} hitsPerPage={manualFiles.length} />
      <NoResultsBoundary fallback={null}>
        <Hits<DocumentHit>
          hitComponent={BoundHitComponentCallback}
          transformItems={transformItems}
          classNames={{
            root: 'contents',
            list: 'contents',
          }}
        />
      </NoResultsBoundary>
    </Index>
  );
});

const SECTION_PAGE_SIZE = 12;

const AutoFilterSectionIndex = React.memo(function AutoFilterSectionIndex({
  indexId,
  manualFiles,
  autoFilter,
  additionalFilters,
  downloadCTA,
  viewCTA,
}: {
  indexId: string;
  manualFiles?: Section['documentFiles'];
  autoFilter: NonNullable<Section['autoFilter']>;
  additionalFilters: string[];
  downloadCTA?: string;
  viewCTA?: string;
}) {
  const BoundHitComponentCallback = React.useCallback(
    ({ hit }: { hit: AlgoliaHit<DocumentHit> }) => (
      <HitComponent hit={hit} downloadCTA={downloadCTA} viewCTA={viewCTA} />
    ),
    [downloadCTA, viewCTA],
  );

  const filterQuery = useMemo(() => {
    const autoQuery = buildAutoFilterQuery(autoFilter);
    let excludeQuery = '';
    if (manualFiles && manualFiles.length > 0) {
      const escapeValue = (v: string) => v?.replace(/"/g, '\\"');
      const excludeParts = manualFiles
        .map((file) => {
          if (!file._ref) return '';
          return `NOT fileId:"${escapeValue(file._ref)}"`;
        })
        .filter(Boolean);
      excludeQuery = excludeParts.join(' AND ');
    }
    const additionalQuery = buildAdditionalFilterQuery(additionalFilters);

    const parts = [autoQuery, excludeQuery, additionalQuery].filter(Boolean);
    return parts.join(' AND ');
  }, [autoFilter, manualFiles, additionalFilters]);

  const manualCount = manualFiles?.length ?? 0;
  const autoHitsPerPage = manualCount < SECTION_PAGE_SIZE ? SECTION_PAGE_SIZE - manualCount : SECTION_PAGE_SIZE;

  if (!filterQuery) return null;

  return (
    <Index indexName={ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.Documents} indexId={`${indexId}__auto`}>
      <Configure filters={filterQuery} />
      <NoResultsBoundary fallback={null}>
        <Hits<DocumentHit>
          hitComponent={BoundHitComponentCallback}
          classNames={{
            root: 'contents',
            list: 'contents',
          }}
        />
      </NoResultsBoundary>
      {/* Kept outside NoResultsBoundary so the auto index's page size is applied
          up front and the load-more control isn't tied to the results boundary. */}
      <AlgoliaShowMore hitPerPage={autoHitsPerPage} incrementBy={SECTION_PAGE_SIZE} className="col-span-full" />
    </Index>
  );
});

const SectionContent = React.memo(function SectionContent({
  section,
  additionalFilters,
  filterActive,
  downloadCTA,
  viewCTA,
}: {
  section: Section;
  additionalFilters: string[];
  filterActive?: boolean;
  downloadCTA?: string;
  viewCTA?: string;
}) {
  const hasManual = !!(section.documentFiles && section.documentFiles.length > 0);
  const hasAuto = !!section.autoFilter;

  if (!hasManual && !hasAuto) {
    return (
      <Typography className="text-center" variant="bodyMd">
        No documents available in this section
      </Typography>
    );
  }

  return (
    <SectionNoResultsBoundary indexId={section.id} hasManual={hasManual} hasAuto={hasAuto}>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 xl:gap-x-5 xl:gap-y-6 gap-4">
        {hasManual && (
          <ManualSectionIndex
            indexId={section.id}
            manualFiles={section.documentFiles!}
            additionalFilters={additionalFilters}
            filterActive={filterActive}
            downloadCTA={downloadCTA}
            viewCTA={viewCTA}
          />
        )}
        {hasAuto && (
          <AutoFilterSectionIndex
            indexId={section.id}
            manualFiles={section.documentFiles}
            autoFilter={section.autoFilter!}
            additionalFilters={additionalFilters}
            downloadCTA={downloadCTA}
            viewCTA={viewCTA}
          />
        )}
      </div>
    </SectionNoResultsBoundary>
  );
});

export function AlgoliaDocumentHub({
  sections,
  className,
  selectedTagIds,
  filterActive,
  downloadCTA,
  viewCTA,
}: DocumentHubProps) {
  const accordionSections = useMemo(() => {
    return sections.map((section) => {
      return {
        id: section.id,
        title: section.title,
        content: (
          <SectionContent
            section={section}
            additionalFilters={filterActive ? selectedTagIds || [] : []}
            filterActive={filterActive}
            downloadCTA={downloadCTA}
            viewCTA={viewCTA}
          />
        ),
      };
    });
  }, [downloadCTA, filterActive, sections, selectedTagIds, viewCTA]);

  const normalizedIds = useMemo(() => {
    const seen = new Map<string, number>();
    return (sections || []).map((s, i) => {
      const count = (seen.get(s.id) ?? 0) + 1;
      seen.set(s.id, count);
      const __kid = count === 1 ? s.id : `${s.id}__${i}`;
      return __kid;
    });
  }, [sections]);

  const [openIds, setOpenIds] = useState<string[]>(normalizedIds);

  const prevFilterRef = useRef({
    selectedTagIds,
    filterActive,
  });

  useEffect(() => {
    const filterChanged =
      prevFilterRef.current.selectedTagIds !== selectedTagIds || prevFilterRef.current.filterActive !== filterActive;

    if (!filterChanged) return;
    prevFilterRef.current = { selectedTagIds, filterActive };

    setOpenIds((prev) =>
      prev.length === normalizedIds.length && prev.every((id, i) => id === normalizedIds[i]) ? prev : normalizedIds,
    );
  }, [selectedTagIds, filterActive, normalizedIds]);

  return (
    <AccordionWithCustomContent
      sections={accordionSections}
      allowMultiple
      className={className}
      openIds={openIds}
      onChange={setOpenIds}
    />
  );
}
